import { AfterViewChecked, Component, OnDestroy, OnInit } from "@angular/core";
import { SafeHtml } from "@angular/platform-browser";
import { Observable, Subscription } from "rxjs";
import { filter } from "rxjs/operators";

import { AnalyticsService } from "@hermes/utils/analytics";
import { FeatureFlagFacade, GRID_FILTER_REVAMP } from "@hermes/states/flipper";
import {
    FilterChangedService,
    GridAlternatives,
    GridFacade,
    GridProduct,
    SearchErrorEvent,
    SearchEvent,
    SearchRefactoredEvent,
} from "@hermes/states/grid-filters-state";
import { stripTags } from "@hermes/utils-generic/helpers";
import { OriginCheckerService } from "@hermes/utils-generic/services/origin-checker";

@Component({
    selector: "h-grid-result-search-header",
    templateUrl: "./grid-result-search-header.component.html",
    styleUrls: ["./grid-result-search-header.component.scss"],
})
export class GridResultSearchHeaderComponent
    implements OnInit, OnDestroy, AfterViewChecked
{
    public total: number | undefined;
    public originalSearch: string | undefined;
    public query: string | undefined;
    public displayedItemNumber!: number;

    /** Duplicated from query - for i18n translation purpose */
    public suggestedTemplate: string | undefined;

    public hideTotalUpdatedItems: boolean = true;

    public isGridFilterRevampEnabled$: Observable<boolean> =
        this.featureFlagFacade.isActivated(GRID_FILTER_REVAMP);

    public sendErrorEvent: boolean = true;
    public searchResultForInnerHtml?: SafeHtml;

    private subscription: Subscription = new Subscription();

    constructor(
        private analyticsService: AnalyticsService,
        private gridFacade: GridFacade,
        private filterChangedService: FilterChangedService,
        private originCheckerService: OriginCheckerService,
        private featureFlagFacade: FeatureFlagFacade,
    ) {}

    public ngOnInit(): void {
        this.subscription.add(
            this.filterChangedService.filterChanged$.subscribe(
                () => (this.hideTotalUpdatedItems = false),
            ),
        );
        this.subscription.add(
            this.gridFacade.total$.subscribe((total: number | undefined) => {
                this.total = total;
            }),
        );
        this.subscription.add(
            this.gridFacade.products$.subscribe(
                (products: GridProduct[] | undefined) => {
                    this.displayedItemNumber = products?.length ?? 0;
                },
            ),
        );
        this.subscription.add(
            this.gridFacade.alternatives$
                .pipe(filter((alternatives) => !!alternatives))
                .subscribe((alternatives: GridAlternatives | undefined) => {
                    this.originalSearch = alternatives?.originalSearch;
                    this.query = alternatives?.resultsFor;
                    this.total = alternatives?.total;
                    this.suggestedTemplate = alternatives?.resultsFor;

                    this.searchResultForInnerHtml = this.buildSearchResult();

                    this.sendAnalyticsEvents();
                }),
        );
    }

    public ngAfterViewChecked(): void {
        if (
            this.sendErrorEvent &&
            this.total === 0 &&
            this.originCheckerService.checkReferrerOrigin()
        ) {
            this.analyticsService.sendData(
                new SearchErrorEvent(this.originalSearch),
            );
        }
        this.sendErrorEvent = false;
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Send analytics events on result page
     */
    public sendAnalyticsEvents(): void {
        if (
            this.total !== 0 &&
            this.originalSearch &&
            this.originCheckerService.checkReferrerOrigin()
        ) {
            if (this.originalSearch === this.suggestedTemplate) {
                this.analyticsService.sendData(
                    new SearchEvent(this.originalSearch),
                );
            } else {
                this.analyticsService.sendData(
                    new SearchRefactoredEvent(
                        this.originalSearch,
                        this.suggestedTemplate,
                    ),
                );
            }
        }
    }

    private buildSearchResult(): string {
        const cleanedTemplate = stripTags(this.suggestedTemplate ?? "");
        return $localize`:@@hermes.grid-page.search.results:Here are the results for « &lt;b&gt;${cleanedTemplate}:INTERPOLATION:&lt;/b&gt; »`;
    }
}
